import type { MeResponse } from '@satisa-memory/shared'
import { readonly, ref } from 'vue'
import { ApiError, api, unwrap } from './api'

// Module-level state: the signed-in identity does not change within a page
// load, so one fetch is shared by every component that asks for it.
const identity = ref<MeResponse | null>(null)
const error = ref<string | null>(null)
const loading = ref(false)
/** No reply and nothing saved: offline, which is not the same as signed out. */
const offline = ref(false)
let started = false

/** Where the browser goes to sign in, returning to the current page after. */
export function loginUrl(): string {
  return `/auth/login?next=${encodeURIComponent(location.pathname + location.search)}`
}

async function load() {
  loading.value = true
  try {
    identity.value = await unwrap(api.api.me.$get())
    error.value = null
    offline.value = false
  } catch (e) {
    identity.value = null
    offline.value = e instanceof ApiError && e.status === 0
    if (e instanceof ApiError && e.status === 401) {
      location.assign(loginUrl())
      return
    }
    error.value = offline.value
      ? 'Offline, and nothing is saved on this device yet. Open Memory once while online; after that the library stays readable offline.'
      : e instanceof ApiError && e.status === 503
        ? 'Sign-in is not configured on this deployment yet (GitHub app or SIGNING_SECRET missing).'
        : e instanceof Error
          ? e.message
          : 'Failed to load identity'
  } finally {
    loading.value = false
  }
}

export function useIdentity() {
  if (!started) {
    started = true
    void load()
  }
  return {
    identity: readonly(identity),
    error: readonly(error),
    loading: readonly(loading),
    offline: readonly(offline),
    reload: load
  }
}
